/***
 * @fetch -> the new Ajax caller
 * @link https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API
 * @AUTHOR Ahmad Jarrar - DbSelection
 */
export default class DbSelection {
    constructor(dbSelection) {
        /***
         * @param dbSelection
         * @type HTML ELEMENT
         * @props selectors
         * @type {{checkBoxesAction: string}}
         */
        this.selectors = {
            checkBoxesAction: '.db-js-check-action',
            tabCheckBoxes: '.tab__pane.active input:not(:disabled)',
            tabSelectedCheckBoxes: '.tab__pane.active input:checked',
            useSelectedDB: '.db-js-use-btn',
            activeTabAjaxData: '.active.db-js-tab-item',
            dbDialog: '.db-js-dialog',
            error: '.db-js-error',
            checkBoxesWrap: '.db-js-checkboxes-wrap',
            dbSelectionButtons: 'db-selection__buttons',
            dbSelectionModalBody: '.db-selection__body',
        };

        this.animationClasses = {
            loading: 'db-selection-loading',
            success: 'db-selection-success',
        };
        this.errorClass = {
            hide: 'db-hide-force',
        };
        this.alerts = {
            checkBoxes: 'Please select at least one database',
        };
        this.fetchError = '';

        this.dbSelection = dbSelection;

        const isIE = UX.test.isIE();
        if (isIE) {
            this.activeTab();
            this.stickyButtons();
        }

        this.selectCheckBoxBtn();
        this.selectCheckBox();
        this.databaseAjax();
    }

    selectCheckBoxBtn() {
        const actionBtns = this._convertToArray(this.dbSelection.querySelectorAll(this.selectors.checkBoxesAction));
        const htmlBtn = {};
        if (!actionBtns.length) return;

        actionBtns.forEach(button => {
            const action = button?.dataset?.action;
            const siteName = button?.dataset?.siteName;
            htmlBtn[action + siteName] = button;
            button.addEventListener('click', this._handleSelectAction.bind(this, button, action, htmlBtn, siteName));
        });
    }

    /***
     * @param button
     * @type HTML ELEMENT
     * @private _handleSelectAction
     */
    _handleSelectAction(button, action, htmlBtn, siteName) {
        const checkBoxes = this._convertToArray(this.dbSelection.querySelectorAll(this.selectors.tabCheckBoxes));
        if (!checkBoxes.length) return;

        switch (action) {
            case 'selectAll':
                this._disableController(htmlBtn, siteName, [true, false, false]);
                checkBoxes.forEach(checkBox => (checkBox.checked = true));
                break;
            case 'clearAll':
                checkBoxes.forEach(checkBox => (checkBox.checked = false));
                this._disableController(htmlBtn, siteName, [false, true, false]);
                break;
            case 'selectAccess':
                this._disableController(htmlBtn, siteName, [false, false, true]);
                checkBoxes.forEach(checkBox => {
                    const available = checkBox?.dataset?.available;
                    if (available && available === 'true') {
                        checkBox.checked = true;
                    } else {
                        checkBox.checked = false;
                    }
                });
        }
    }

    /****
     *
     * @param element
     * @param siteName
     * @param values ['selectAll' , 'clearAll' , 'selectAccess'] || true or false values
     * @private
     */
    _disableController(element, siteName, values) {
        const labels = ['selectAll', 'clearAll', 'selectAccess'];
        labels.forEach((label, index) => {
            element[label + siteName] ? (element[label + siteName].disabled = values[index]) : false;
        });
    }

    selectCheckBox() {
        /***
         * @info Event delegation
         * @link https://javascript.info/event-delegation
         */
        const checkBoxesWrap = this._convertToArray(this.dbSelection.querySelectorAll(this.selectors.checkBoxesWrap));
        if (!checkBoxesWrap.length) return;
        checkBoxesWrap.forEach(element => {
            element.addEventListener('click', this._removeDisableFromButton.bind(this));
        });
    }

    _removeDisableFromButton(event) {
        const siteName = event.currentTarget?.dataset?.siteName;
        if (
            siteName &&
            !event.target?.disabled &&
            event.target?.localName === 'input' &&
            event.target?.type === 'checkbox'
        ) {
            const disabledAction = this.dbSelection.querySelector(
                `${this.selectors.checkBoxesAction}[data-site-name=${siteName}]:disabled`
            );
            if (disabledAction?.disabled) {
                disabledAction.disabled = false;
            }
        }
    }

    /***
     * @INFO AJAX EON BUTTON EVENT
     */
    databaseAjax() {
        const useSelectedDB = this._convertToArray(this.dbSelection.querySelectorAll(this.selectors.useSelectedDB));
        if (!useSelectedDB) return;

        useSelectedDB.forEach(button => {
            button.addEventListener('click', this._handleAjax.bind(this));
        });
    }

    /***
     * @INFO AJAX CALL FUNCTION
     * @private
     */
    _handleAjax() {
        const {ajaxUrl, href, checkBoxesError} = this._buildAjaxUrl() || {};
        const error = this.dbSelection.querySelector(this.selectors.error);
        if (checkBoxesError && (error?.textContent === '' || this.fetchError === error?.textContent)) {
            error.textContent = checkBoxesError;
            this.fetchError = '';
            error.parentElement.classList.remove(this.errorClass.hide);
            return;
        } else if (!checkBoxesError && !this.fetchError) {
            error.textContent = '';
            error.parentElement.classList.add(this.errorClass.hide);
        }

        if (ajaxUrl && href && error?.textContent === '') {
            const dbDialog = this.dbSelection.querySelector(this.selectors.dbDialog);
            dbDialog?.classList.add(this.animationClasses.loading);
            fetch(ajaxUrl)
                .then(response => response.text())
                .then(data => {
                    if (data.trim() === 'SUCCESS') {
                        dbDialog.classList.add(this.animationClasses.success);
                        error.parentElement.classList.add(this.errorClass.hide);
                        this.fetchError = '';
                        location.href = href;
                    } else {
                        dbDialog.classList.remove(this.animationClasses.loading);
                        error.textContent = data;
                        error.parentElement.classList.remove(this.errorClass.hide);
                        this.fetchError = data;
                    }
                })
                .catch(error => {
                    dbDialog.classList.remove(this.animationClasses.loading);
                    this.fetchError = error;
                    error.parentElement.classList.remove(this.errorClass.hide);
                });
        }
    }

    /***
     * @INFO BUILD AJAX LINK & RETURN HREF
     * @returns {{ajaxUrl: string, href: string}}
     * @private
     */
    _buildAjaxUrl() {
        const activeTab = this.dbSelection.querySelector(this.selectors.activeTabAjaxData);
        const href = activeTab?.dataset?.href;
        const widgetId = activeTab?.dataset?.id;
        const pbContext = document.head?.querySelector('[name="pbContext"]')?.content;
        const selectedCheckBox = this._convertToArray(
            this.dbSelection.querySelectorAll(this.selectors.tabSelectedCheckBoxes)
        );
        const siteCode = activeTab?.dataset?.siteCode;
        let stringSelectedCheckBox = null;
        let checkBoxesError = null;
        if (selectedCheckBox.length) {
            stringSelectedCheckBox = selectedCheckBox
                .map(checkBox => {
                    if (checkBox?.dataset?.id) return `dbId=${checkBox.dataset.id}`;
                })
                .join('&');
        } else {
            checkBoxesError = this.alerts.checkBoxes;
        }

        if (!widgetId && !pbContext && !siteCode) return null;

        return {
            ajaxUrl: `/pb/widgets/dbSelection?widgetId=${widgetId}&pbContext=${pbContext}&${stringSelectedCheckBox}&websiteCode=${siteCode}`,
            href,
            checkBoxesError,
        };
    }
    /***
     * @param nodeList
     * @returns {[]}
     * @private
     */
    _convertToArray(nodeList) {
        return Array.prototype.slice.call(nodeList);
    }

    activeTab() {
        const tabItems = this._convertToArray(this.dbSelection.querySelectorAll('.tab__nav__item__link'));
        tabItems.forEach(tabItem => {
            tabItem.addEventListener('click', this.stickyButtons.bind(this, tabItem));
        });
    }

    stickyButtons(tabItem) {
        const dbSelectionModalBody = this.dbSelection.querySelector(this.selectors.dbSelectionModalBody);
        if (!dbSelectionModalBody) return;

        let activePaneButtons = this.dbSelection.querySelector('.tab__pane.active .db-selection__buttons');
        activePaneButtons.classList.add('fixed');

        dbSelectionModalBody.addEventListener('scroll', function (e) {
            activePaneButtons.style.bottom = -1 * dbSelectionModalBody.scrollTop + 'px';
        });
    }
}
